export const DEFAULT_LOCALE = 'en';

const resolved = new Map<string, string>();

// Deliberately separate from `resolved`: the memo cache is populated by every caller, including
// the ones that pass no `onInvalid`. Reusing it as the report guard would mean a hookless caller
// warming the cache first (this repo's own date dropdown does exactly that) permanently silences
// the consumer's telemetry for that value.
const reported = new Set<string>();

function isCanonicalizable(locale: string): boolean {
  try {
    Intl.getCanonicalLocales(locale);
    return true;
  } catch {
    return false;
  }
}

/**
 * Structural BCP-47 validity.
 *
 * Rejects the literal strings `"null"`/`"undefined"` that reach us from query params without
 * special-casing them — `Intl.getCanonicalLocales` already rejects any malformed tag. Note this
 * is a *shape* check only: a well-formed but unsupported tag (`xx-XX`) passes.
 */
export function isValidLocale(locale?: string | null): locale is string {
  return !!locale && isCanonicalizable(locale);
}

/**
 * Returns `locale` if it is a valid BCP-47 tag, otherwise DEFAULT_LOCALE.
 *
 * Passing an invalid tag to an `Intl` API throws `RangeError`, which is how a poisoned
 * `i18n.language` of `"null"` once took down a whole page — so every locale reaching an `Intl`
 * call should come through here.
 *
 * Callers hit this once per rendered row (a list of dates formats one per item), so the result is
 * memoised per input string.
 *
 * @param onInvalid Optional report hook for a rejected tag — consumer-supplied so this module
 * doesn't inherit any one app's telemetry choices (the same split as `RecoveryOptions.report` in
 * `dynamic-import-recovery.ts`). It fires at most once per distinct bad tag — so a poisoned locale
 * reports once, not on every re-render — and is tracked independently of the memo cache, so an
 * earlier hookless call cannot swallow the report.
 */
export function getSafeLocale(locale?: string | null, onInvalid?: (locale: string) => void): string {
  if (!locale) {
    return DEFAULT_LOCALE;
  }
  const cached = resolved.get(locale);
  const safe = cached ?? (isValidLocale(locale) ? locale : DEFAULT_LOCALE);
  if (cached === undefined) {
    resolved.set(locale, safe);
  }
  if (safe !== locale && onInvalid && !reported.has(locale)) {
    reported.add(locale);
    onInvalid(locale);
  }
  return safe;
}
